//
// Imports
//

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

// Constants
import {
  getSampleInfoBasicHeaders,
  getSampleInfoInstrumentsHeaders,
  getSampleInfoResultsSetsHeaders,
} from "../../../constants/tableHeaders";

// Types
import type { FlagItem, PendingResult, PendingSample } from "../../../services/api/domains/pending/pending.types";
import type { Visit } from "../../../services/api/domains/lis/lis.types";
import { useFlagsDialog } from "./useFlagsDialog";

//
// Types
//

export interface SampleInfoInstrument {
  id: number;
  name: string;
  location?: string;
  serial?: string;
  channels?: Array<{
    id: number;
    name: string;
    port: number;
  }>;
  resultsCount: number;
  tests: SampleInfoTest[];
}

export interface SampleInfoTest {
  testCode: string;
  lisName?: string;
  hasMappingKey: boolean;
  result?: string;
  resultTime?: string | Date;
  units?: string;
  rangeText?: string;
  flagLevel?: number;
  flags?: FlagItem[];
}

export interface SampleInfoResultsSet {
  setId: number;
  resultsCount: number;
  resultTime?: string | Date;
  instrumentName: string;
  instrumentLocation?: string;
  instrumentSerial?: string;
  instrumentChannels?: Array<{
    id: number;
    name: string;
    port: number;
  }>;
  tests: SampleInfoTest[];
}

export interface SampleInfoData {
  barcode: string;
  typeCode?: string;
  typeName?: string;
  visitId?: string;
  taken?: string | Date;
  isControl: boolean;
  color?: any;
  totalResults: number;
  instruments: SampleInfoInstrument[];
  resultsSets: SampleInfoResultsSet[];
  pendingResults: PendingResult[];
}

export interface SampleInfoTableItem {
  instrumentName?: string;
  instrumentLocation?: string;
  instrumentSerial?: string;
  channels?: Array<{ id: number; name: string; port: number }>;
  setId?: number;
  testCode: string;
  testName: string;
  result?: string;
  resultTime?: string | Date;
  units?: string;
  rangeText?: string;
  flagLevel?: number;
  flags?: FlagItem[];
  hasMappingKey: boolean;
  key: string;
}

export interface SampleInfoInstrumentSummary {
  instrumentName: string;
  instrumentLocation?: string;
  diskOrRack?: string;
  position?: string;
  sampleNumber?: string;
  sampleType: string;
  setsCount: number;
  pendingCount: number;
  approvedCount: number;
  key: string;
}

export const useSampleInfoDialog = (
  key: string = "default",
  pendingComposable: ReturnType<typeof usePending>,
  lisComposable: ReturnType<typeof useLis>,
) => {
  //
  // Composables
  //

  // Localization
  const { t } = useI18n();

  // Breakpoints
  const { smAndDown } = useDisplay();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  // Flagging
  const { getFlagGradient } = useFlagging();

  // Tests
  const testsComposable = useTests();

  //
  // State (isolated by key)
  //

  const isOpen = useState<boolean>(`sampleInfo.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`sampleInfo.${key}.loading`, () => false);
  const selectedSample = useState<PendingSample | null>(`sampleInfo.${key}.selectedSample`, () => null);
  const data = useState<SampleInfoData | null>(`sampleInfo.${key}.data`, () => null);
  const visitData = useState<Visit | null>(`sampleInfo.${key}.visitData`, () => null);
  const activeTab = useCookie<string>(`sampleInfo.${key}.activeTab`, {
    default: () => "instruments",
    maxAge: 60 * 60 * 24 * 7,
  });
  const flagsDialog = useFlagsDialog(`${key}.flagsDialog`);

  //
  // Computed Properties
  //

  const basicInfoHeaders = computed(() => getSampleInfoBasicHeaders(t, smAndDown.value));

  const instrumentsHeaders = computed(() => getSampleInfoInstrumentsHeaders(t, smAndDown.value));

  const resultsSetsHeaders = computed(() => getSampleInfoResultsSetsHeaders(t, smAndDown.value));

  const dialogTitle = computed(() => {
    if (data.value?.barcode) {
      return t("components.common.dialogs.titles.sample_info", {
        barcode: data.value.barcode,
      });
    }
    return t("components.common.dialogs.messages.loading_info");
  });

  /**
   * Transform instruments data into summary rows for v-data-table
   *
   * Each row shows:
   * - Instrument name
   * - Disk/Rack, Position, Sample Number (when available)
   * - Sample type
   * - Count of result sets
   * - Count of pending results
   * - Count of approved results (from visit examinations)
   */
  const instrumentsTableItems = computed<SampleInfoInstrumentSummary[]>(() => {
    if (!data.value) return [];

    // Step 1: Count result sets per instrument (from pending results)
    const resultSetsByInstrument = new Map<string, Set<number>>();
    data.value.resultsSets.forEach((set) => {
      const instrKey = set.instrumentName;
      if (!resultSetsByInstrument.has(instrKey)) {
        resultSetsByInstrument.set(instrKey, new Set());
      }
      resultSetsByInstrument.get(instrKey)!.add(set.setId);
    });

    // Step 2: Count pending results per instrument (already grouped)
    const pendingByInstrument = new Map<string, number>();
    data.value.instruments.forEach((instrument) => {
      pendingByInstrument.set(instrument.name, instrument.resultsCount);
    });

    // Step 3: Count approved results (GLOBAL)
    //
    // IMPORTANT:
    // Approved results cannot be grouped per instrument without backend support.
    // LIS examinations do not expose instrument identifiers,
    // so any per-instrument grouping would be a guess.
    const approvedGlobalCount = visitData.value?.examinations
      ? visitData.value.examinations.reduce((acc: number, exam: any) => acc + (exam.items?.length || 1), 0)
      : 0;

    // Step 4: Build instrument summary rows
    const allInstrumentNames = new Set<string>();
    data.value.instruments.forEach((i) => allInstrumentNames.add(i.name));

    return Array.from(allInstrumentNames)
      .map((instrumentName, index) => {
        const pendingInstrument = data.value!.instruments.find((i) => i.name === instrumentName);

        const setsCount = resultSetsByInstrument.get(instrumentName)?.size || 0;
        const pendingCount = pendingByInstrument.get(instrumentName) || 0;

        // explicit guard
        if (setsCount === 0 && pendingCount === 0) {
          return null;
        }

        return {
          instrumentName,
          instrumentLocation: pendingInstrument?.location || undefined,
          diskOrRack: undefined,
          position: undefined,
          sampleNumber: undefined,
          sampleType: data.value!.typeName || data.value!.typeCode || "",
          setsCount,
          pendingCount,
          approvedCount: approvedGlobalCount,
          key: `instrument_summary_${index}`,
        };
      })
      .filter(Boolean) as SampleInfoInstrumentSummary[];
  });

  /**
   * Transform results sets data for v-data-table
   */
  const resultsSetsTableItems = computed<SampleInfoTableItem[]>(() => {
    if (!data.value) return [];

    return data.value.resultsSets.flatMap((set, setIndex) =>
      set.tests.map((test, testIndex) => ({
        setId: set.setId,
        testCode: test.testCode,
        testName: test.lisName || test.testCode,
        result: test.result,
        resultTime: test.resultTime,
        units: test.units,
        rangeText: test.rangeText,
        flagLevel: test.flagLevel,
        flags: test.flags ?? [],
        hasMappingKey: test.hasMappingKey,
        key: `set_${setIndex}_${testIndex}`,
      })),
    );
  });

  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    sampleInfo: data.value,
    visitData: visitData.value,
    loading: loading.value,
    activeTab: activeTab.value,
    dialogTitle: dialogTitle.value,
    basicInfoHeaders: basicInfoHeaders.value,
    instrumentsHeaders: instrumentsHeaders.value,
    resultsSetsHeaders: resultsSetsHeaders.value,
    instrumentsTableItems: instrumentsTableItems.value,
    resultsSetsTableItems: resultsSetsTableItems.value,
    flagsDialogProps: {
      isOpen: flagsDialog.isOpen.value,
      flags: flagsDialog.flags.value,
      flagsHeaders: flagsDialog.flagsHeaders.value,
      onClose: flagsDialog.close,
    },
    onClose: close,
    onTabChange: (tab: string) => (activeTab.value = tab),
    getSetMetadata,
    getSetResultsCount,
    getSampleTypeColor,
    getFlagGradient,
    onViewFlags: flagsDialog.open,
  }));

  //
  // Private Methods - Data Fetching
  //

  /**
   * Step 1: Prepare examination and flagging data
   */
  const prepareExaminationsAndFlags = async (visit: any, results: PendingResult[], lisComposable: any) => {
    // Flatten examinations for easier lookup
    const flatExaminations = visit?.examinations
      ? visit.examinations.flatMap((exam: any) => [exam, ...(exam.items || [])])
      : [];

    // Calculate flagging
    let flaggingData: any[] = [];
    if (visit?.patient && flatExaminations.length > 0) {
      const flaggingRequest = results
        .map((r) => {
          const exam = flatExaminations.find((e: any) => e.id === r.mappingKey);
          if (!exam) return null;

          return {
            patient: {
              born: visit.patient.born,
              isMale: visit.patient.isMale,
            },
            test: {
              id: exam.id,
              result: r.displayResult,
            },
            refId: r.id.toString(),
          };
        })
        .filter(Boolean);

      if (flaggingRequest.length > 0) {
        try {
          const flagging = await lisComposable.calculateFlagging(flaggingRequest);
          if (flagging) flaggingData = flagging;
        } catch (error) {
          console.error("[useSampleInfoDialog] Flagging error:", error);
        }
      }
    }

    return { flatExaminations, flaggingData };
  };

  /**
   * Map raw ResultFlag[] from the API model to FlagItem[] for display.
   */
  const mapInstrumentFlags = (rawFlags: any[] | undefined): FlagItem[] => {
    if (!rawFlags?.length) return [];
    return rawFlags.map((f) => ({
      // severity: 0=info,1=warning,2=danger,3=panic → FlagItem level uses same numeric scale
      level: (f.severity ?? 0) + 1, // shift so info=1, warning=2, danger=3, panic=4
      source: f.isInstrumentFlag ? ("Instrument" as const) : ("Processor" as const),
      code: f.code ?? "",
      description: f.description ?? "",
    })) as FlagItem[];
  };

  /**
   * Step 3: Enrich a single result with all metadata
   */
  const enrichResult = (
    result: PendingResult,
    test: any,
    flatExaminations: any[],
    flaggingData: any[],
  ): SampleInfoTest => {
    const exam = flatExaminations.find((e) => e.id === result.mappingKey);
    const flag = flaggingData.find((f) => f.refId === result.id.toString());

    return {
      testCode: result.testCode ?? "",
      lisName: result.lisName ?? "",
      hasMappingKey: !!result.mappingKey,
      result: result.displayResult,
      resultTime: result.resultTime,
      units: exam?.units || "",
      rangeText: exam?.rangeText || "",
      flagLevel: flag?.calculationResult?.flagLevel || 0,
      flags: mapInstrumentFlags((result as any).flags),
    };
  };

  /**
   * Step 4: Group results by instrument
   */
  const groupByInstrument = (
    results: PendingResult[],
    allTests: any[],
    flatExaminations: any[],
    flaggingData: any[],
  ): Map<number, SampleInfoInstrument> => {
    const instrumentsMap = new Map<number, SampleInfoInstrument>();

    results.forEach((result) => {
      const test = allTests.find((t) => t.testCode === result.testCode);
      if (!test) return;

      const { instrumentId, instrument } = test;

      // Create instrument entry if doesn't exist
      if (!instrumentsMap.has(instrumentId)) {
        instrumentsMap.set(instrumentId, {
          id: instrumentId,
          name: instrument.name,
          location: instrument.location?.name,
          serial: instrument.instrumentSerialOrTag,
          channels: instrument.channels,
          resultsCount: 0,
          tests: [],
        });
      }

      // Add enriched test to instrument
      const instrumentData = instrumentsMap.get(instrumentId)!;
      instrumentData.resultsCount++;
      instrumentData.tests.push(enrichResult(result, test, flatExaminations, flaggingData));
    });

    return instrumentsMap;
  };

  /**
   * Step 5: Group results by results set (batch)
   */
  const groupByResultsSet = (
    results: PendingResult[],
    allTests: any[],
    flatExaminations: any[],
    flaggingData: any[],
  ): Map<number, SampleInfoResultsSet> => {
    const resultsSetsMap = new Map<number, SampleInfoResultsSet>();

    results.forEach((result) => {
      const test = allTests.find((t) => t.testCode === result.testCode);
      if (!test) return;

      const { instrument } = test;

      // Create results set entry if doesn't exist
      if (!resultsSetsMap.has(result.resultsSetId)) {
        resultsSetsMap.set(result.resultsSetId, {
          setId: result.resultsSetId,
          resultsCount: 0,
          resultTime: result.resultTime,
          tests: [],
          instrumentName: instrument.name,
          instrumentLocation: instrument.location?.name,
          instrumentSerial: instrument.instrumentSerialOrTag,
          instrumentChannels: instrument.channels,
        });
      }

      // Add enriched test to results set
      const setData = resultsSetsMap.get(result.resultsSetId)!;
      setData.resultsCount++;
      setData.tests.push(enrichResult(result, test, flatExaminations, flaggingData));
    });

    return resultsSetsMap;
  };

  /**
   * Main: Build enriched sample info from multiple data sources
   *
   * Data flow:
   * 1. Fetch: Tests, Visit, Results
   * 2. Prepare: Examinations & Flagging
   * 3. Enrich: Add units, ranges, flags to each result
   * 4. Group: By instrument & by results set
   * 5. Return: Structured data
   */
  const buildSampleInfo = async (sample: PendingSample, results: PendingResult[]): Promise<SampleInfoData | null> => {
    if (!results?.length) return null;

    // TODO(v0.8.0): The global /tests endpoint has been removed. Tests are now per-instrument.
    // Until the SampleInfoDialog receives an instrumentId, test enrichment is skipped.
    const [allTests, visit] = await Promise.all([
      Promise.resolve([] as typeof testsComposable.tests.value),
      lisComposable.fetchVisitBySample(sample.barcode),
    ]);

    // Store visit data
    visitData.value = visit;

    // Step 2: Prepare examinations and flagging
    const { flatExaminations, flaggingData } = await prepareExaminationsAndFlags(visit, results, lisComposable);

    // Step 3 & 4: Group and enrich results
    const instrumentsMap = groupByInstrument(results, [...allTests], flatExaminations, flaggingData);

    const resultsSetsMap = groupByResultsSet(results, [...allTests], flatExaminations, flaggingData);

    // Step 5: Return structured data
    return {
      barcode: sample.barcode,
      typeCode: sample.typeCode,
      typeName: sample.typeName,
      visitId: visit?.id || sample.visitId,
      taken: sample.taken,
      isControl: sample.isControl,
      color: sample.color,
      totalResults: results.length,
      instruments: Array.from(instrumentsMap.values()),
      resultsSets: Array.from(resultsSetsMap.values()),
      pendingResults: results,
    };
  };

  /**
   * Load sample info data
   */
  const loadData = async (sample: PendingSample): Promise<void> => {
    loading.value = true;

    try {
      const results = await pendingComposable.fetchPendingSampleResults(sample.barcode);

      if (results) {
        const info = await buildSampleInfo(sample, results);
        data.value = info;
      }
    } catch (error) {
      console.error("[useSampleInfoDialog] loadData error:", error);
      showSnackbar(t("pages.validation.dialogs.sampleInfo.loadError"), {
        icon: "mdi-alert-circle",
        color: "error",
      });
    } finally {
      loading.value = false;
    }
  };

  //
  // Public Methods - Helpers
  //

  /**
   * Get instrument metadata count
   */
  const getInstrumentResultsCount = (instrumentName: string): number => {
    const instrument = data.value?.instruments.find((i) => i.name === instrumentName);
    return instrument?.resultsCount || 0;
  };

  /**
   * Get results set count
   */
  const getSetResultsCount = (setId: number): number => {
    const set = data.value?.resultsSets.find((s) => s.setId === setId);
    return set?.resultsCount || 0;
  };

  const getSampleTypeColor = (color?: any) => {
    if (!color) return "primary";
    const { red, green, blue, alpha } = color;
    return `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
  };

  /**
   * Format instrument metadata for display
   * Example: "Ботевград"
   */
  const getInstrumentMetadata = (instrumentName: string): string => {
    const instrument = data.value?.instruments.find((i) => i.name === instrumentName);

    const parts: string[] = [];
    if (instrument?.location) parts.push(instrument.location);

    return parts.join("\u00A0\u00A0/\u00A0\u00A0");
  };

  /**
   * Format results set metadata for display
   * Example: "Cobas Pure / SN: 12345 / Channel 1, 2 / Ботевград / 20.03.2025 г. | 13:05"
   */
  const getSetMetadata = (setId: number): string => {
    const set = data.value?.resultsSets.find((s) => s.setId === setId);
    if (!set) return "";

    const parts: string[] = [];

    if (set.instrumentName) parts.push(set.instrumentName);
    if (set.instrumentSerial) parts.push(`SN: ${set.instrumentSerial}`);
    if (set.instrumentChannels?.length) {
      parts.push(set.instrumentChannels.map((c) => c.name).join(", "));
    }
    if (set.instrumentLocation) parts.push(set.instrumentLocation);
    if (set.resultTime) {
      const { date, time } = formatDateTime(set.resultTime);
      parts.push(`${date} | ${time}`);
    }

    return parts.join("\u00A0\u00A0/\u00A0\u00A0");
  };

  //
  // Public Methods - Dialog Control
  //

  /**
   * Open dialog and load sample info
   */
  const open = async (sample: PendingSample): Promise<void> => {
    selectedSample.value = sample;
    activeTab.value = "instruments";
    isOpen.value = true;
    await loadData(sample);
  };

  /**
   * Close dialog and clear data
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — old data stays visible during
    // the close animation. State will be replaced by the next open() call.
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading: readonly(loading),
    selectedSample: readonly(selectedSample),
    data: readonly(data),
    visitData: readonly(visitData),
    activeTab: readonly(activeTab),

    // Computed
    dialogTitle: readonly(dialogTitle),
    basicInfoHeaders: readonly(basicInfoHeaders),
    instrumentsHeaders: readonly(instrumentsHeaders),
    resultsSetsHeaders: readonly(resultsSetsHeaders),
    instrumentsTableItems: readonly(instrumentsTableItems),
    resultsSetsTableItems: readonly(resultsSetsTableItems),
    componentProps,

    // Methods
    open,
    close,
    getInstrumentResultsCount,
    getSetResultsCount,
    getInstrumentMetadata,
    getSetMetadata,
    getFlagGradient,
  };
};
